/**
 * <SolomonPlannerComposer />
 *
 * The user-facing front of the Solomon Action Engine. Composes:
 *
 *   useSolomonPlanner  →  proposals      ┐
 *                                         ├─→  <ActionProposalCard /> per
 *   useActionEngine   →  prepare/execute ┘
 *
 * Behavior:
 *   1. User types an utterance, submits.
 *   2. We call the planner; expect one of:
 *        - proposals[]                 → prepare each → render cards
 *        - clarifying_question         → show as an assistant note
 *        - freeform_reply              → show as an assistant note
 *        - error                       → show inline error note
 *   3. Each card has its own Confirm / Dismiss. Confirming runs the
 *      executor through the engine's audit pipe and surfaces a
 *      toast (with one-click undo when applicable).
 *
 * Why a separate component (not inlined in SolomonPanel)?
 * -------------------------------------------------------
 * - Reusable: a future "Quick action" button on PersonProfile,
 *   GroupDetail, or the workflow runner's "suggested follow-ups"
 *   surface can mount the same component with different context
 *   hints — same engine, same audit, same undo.
 * - Testable: the composer's state machine (input → planner →
 *   cards → execute) is testable in isolation without the rest of
 *   the panel.
 * - Additive: SolomonPanel's existing chat composer keeps working
 *   unchanged; this component is purely incremental.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, MessageCircleQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useSolomonPlanner } from "@/hooks/useSolomonPlanner";
import { useOrganization } from "@/hooks/useOrganization";
import type { PlanContextHints } from "@/hooks/useSolomonPlanner";
import { useActionEngine } from "@/lib/solomon-action-engine";
import type { PreparedAction } from "@/lib/solomon-action-engine";
import { useSolomonContext } from "@/components/solomon/SolomonContext";
import { ActionProposalCard } from "./ActionProposalCard";

interface SolomonPlannerComposerProps {
  /**
   * Optional context to send with each plan request — page path,
   * focused person, etc. The hook auto-fills page from
   * useLocation, so callers usually only need to pass focus hints.
   */
  context?: PlanContextHints;
  /** Custom placeholder, e.g. "Tag, remind, or follow up…" */
  placeholder?: string;
  /**
   * Optional callback after a card's execute completes (success or
   * failure). The parent can use this to close a dialog, refetch
   * data, etc.
   */
  onAfterExecute?: (cardId: string, ok: boolean) => void;
  className?: string;
}

/**
 * A note we surface above the input when the planner answered
 * without proposals — either a clarifying question or a freeform
 * reply. Distinct from `error` because these are normal model
 * outputs, not failures.
 */
interface AssistantNote {
  kind: "freeform" | "clarifying";
  text: string;
  plannedForOrganizationId: string;
}

export function SolomonPlannerComposer({
  context,
  placeholder = "Try: 'remind me to follow up with Sarah next Tuesday'",
  onAfterExecute,
  className,
}: SolomonPlannerComposerProps) {
  const [input, setInput] = useState("");
  const [cards, setCards] = useState<
    Array<{
      id: string;
      prepared: PreparedAction;
      plannedForOrganizationId: string;
    }>
  >([]);
  const [note, setNote] = useState<AssistantNote | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { plan, isPlanning } = useSolomonPlanner();
  const { organizationId } = useOrganization();
  const { prepare, executePrepared, isExecuting } = useActionEngine({
    defaultSource: "solomon",
  });
  // Page-level focus pushed via `useSolomonFocus` on entity pages
  // (PersonProfile, etc.). Merged below — prop wins per-field so
  // callers can override page context if needed.
  const { focusContext: providerFocus } = useSolomonContext();
  const priorOrganizationIdRef = useRef(organizationId);

  // Prepared cards and assistant notes can contain tenant-scoped names and
  // model text. Discard them as soon as the resolved tenant changes; the
  // action engine already blocks execution, but rendering old content is not
  // safe either.
  useEffect(() => {
    if (priorOrganizationIdRef.current === organizationId) return;
    priorOrganizationIdRef.current = organizationId;
    setCards([]);
    setNote(null);
    setErrorMessage(null);
  }, [organizationId]);

  // Do not rely only on the cleanup effect above: effects run after a render
  // has committed, so an organization switch could otherwise paint a prior
  // tenant's model text or action preview for one frame. Each planner result
  // is server-bound to a tenant; render stored state only while that binding
  // still agrees with the resolved active tenant.
  const visibleCards = cards.filter(
    (card) => card.plannedForOrganizationId === organizationId,
  );
  const visibleNote =
    note?.plannedForOrganizationId === organizationId ? note : null;

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const utterance = input.trim();
      if (!utterance) return;

      // Reset prior state. We deliberately don't accumulate cards
      // across turns — each utterance is a fresh proposal cycle.
      // If the user changes their mind mid-confirm, they re-type;
      // the prior card list disappears.
      setCards([]);
      setNote(null);
      setErrorMessage(null);

      // Merge: provider focus first, prop wins per-field. Spread
      // order matters — `...context` last means an explicit prop
      // value for `focusedPersonId` overrides the page-registered
      // one. Both may be undefined (e.g. global sheet on a
      // non-entity page); in that case we pass `undefined` so the
      // hook lets useLocation auto-fill `page` only.
      const mergedContext: PlanContextHints | undefined =
        providerFocus || context
          ? { ...(providerFocus ?? {}), ...(context ?? {}) }
          : undefined;
      const result = await plan({ utterance, context: mergedContext });
      if (!result) {
        setErrorMessage(
          "Solomon couldn't process that right now. Please try again.",
        );
        return;
      }

      if (result.proposals.length > 0) {
        // Prepare every proposal in parallel — preview lookups are
        // mostly Supabase round-trips, so doing them concurrently
        // is meaningfully faster on cards with N>1.
        const prepared = await Promise.all(
          result.proposals.map((p) => prepare(p)),
        );
        setCards(
          prepared.map((p, i) => ({
            id: `${p.definition?.id ?? "unknown"}-${i}-${Date.now()}`,
            prepared: p,
            plannedForOrganizationId: result.plannedForOrganizationId,
          })),
        );
        // Clear the input only after we have something to show —
        // gives the user a clear visual handoff from typing to
        // reviewing.
        setInput("");
        return;
      }

      if (result.clarifying_question) {
        setNote({
          kind: "clarifying",
          text: result.clarifying_question,
          plannedForOrganizationId: result.plannedForOrganizationId,
        });
        return;
      }

      if (result.freeform_reply) {
        setNote({
          kind: "freeform",
          text: result.freeform_reply,
          plannedForOrganizationId: result.plannedForOrganizationId,
        });
        return;
      }

      // Defensive: planner returned nothing of substance. Treat as
      // a soft error.
      setErrorMessage("Solomon didn't have a response for that.");
    },
    [input, context, providerFocus, plan, prepare],
  );

  const handleConfirm = useCallback(
    async (cardId: string, prepared: PreparedAction) => {
      const result = await executePrepared(prepared);
      // Remove the card whether the action succeeded or failed; the
      // engine's toast covers the user-facing outcome, and a failed
      // card stuck on screen would be confusing.
      setCards((prev) => prev.filter((c) => c.id !== cardId));
      onAfterExecute?.(cardId, result.ok);
    },
    [executePrepared, onAfterExecute],
  );

  const handleDismiss = useCallback((cardId: string) => {
    setCards((prev) => prev.filter((c) => c.id !== cardId));
  }, []);

  return (
    <div className={cn("space-y-3", className)}>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder={placeholder}
          disabled={isPlanning}
          aria-label="What should Solomon do?"
        />
        <Button
          type="submit"
          size="icon"
          disabled={isPlanning || !input.trim()}
          aria-label="Plan action"
        >
          {isPlanning ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </form>

      {/* Assistant-style note for freeform / clarifying replies. */}
      {visibleNote && (
        <div
          className={cn(
            "flex items-start gap-2 rounded-lg border p-3 text-sm",
            visibleNote.kind === "clarifying"
              ? "border-info-500/40 bg-info-500/10"
              : "border-border/60 bg-muted/60",
          )}
          role="status"
        >
          {visibleNote.kind === "clarifying" ? (
            <MessageCircleQuestion className="h-4 w-4 mt-0.5 flex-shrink-0 text-info-600" />
          ) : (
            <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary" />
          )}
          <p className="text-sm text-foreground">{visibleNote.text}</p>
        </div>
      )}

      {errorMessage && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {errorMessage}
        </div>
      )}

      {/* Proposal cards. Each owns its own confirm/dismiss controls. */}
      {visibleCards.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Proposed action{visibleCards.length > 1 ? "s" : ""}
          </p>
          {visibleCards.map(({ id, prepared }) => (
            <ActionProposalCard
              key={id}
              prepared={prepared}
              onConfirm={(p) => handleConfirm(id, p)}
              onDismiss={() => handleDismiss(id)}
              isExecuting={isExecuting}
            />
          ))}
        </div>
      )}
    </div>
  );
}
